import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { useAdmin } from '../../hooks/useAdmin';
import { formatters } from '../../utils/dateUtils';
import { getUsernameById } from './functions';
import { MarkdownDisplay, QuestLabel, FilterTabs } from '../shared';
import AdminListItem from './AdminListItem';
import AdminEmptyState from './AdminEmptyState';
import AdminPageLayout from './AdminPageLayout';
import { getLocalizedText } from '../../utils/localizationHelpers';

export default function AdminQuestApproval({ 
  questRequests = [], 
  pendingQuestRequests = [], 
  requestsLoading = false, 
  users = [] 
}) {
  const { t } = useTranslation();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const {
    approveQuestRequest,
    rejectQuestRequest,
    isAdmin,
    adminLoading,
  } = useAdmin();

  const getFilteredRequests = () => {
    switch (filterStatus) {
      case 'pending':
        return pendingQuestRequests;
      case 'approved':
        return questRequests.filter(r => r.status === 'approved');
      case 'rejected':
        return questRequests.filter(r => r.status === 'rejected');
      default:
        return questRequests;
    }
  };

  const filteredRequests = getFilteredRequests();

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await approveQuestRequest(selectedRequest.id, approvalMessage);
      setShowApprovalModal(false);
      setApprovalMessage('');
      setSelectedRequest(null);
    } catch (error) {
      console.error('Failed to approve request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await rejectQuestRequest(selectedRequest.id, rejectionMessage);
      setShowRejectionModal(false);
      setRejectionMessage('');
      setSelectedRequest(null);
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Show loading if admin status is still being checked or if requests are loading
  if (adminLoading || requestsLoading) {
    return (
      <div className="admin-loading">
        <FontAwesomeIcon icon={ICONS.SPINNER} spin />
        <p>{adminLoading ? 'Checking admin status...' : t('admin.questApproval.loadingRequests')}</p>
      </div>
    );
  }

  // Show error if not admin (shouldn't happen since AdminPortal checks this, but just in case)
  if (!isAdmin) {
    return (
      <div className="admin-error">
        <p>Access denied. Admin privileges required.</p>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title={t('admin.questApproval.title')}
      className="admin-quest-approval"
      headerActions={
        <FilterTabs
          tabs={[
            { id: 'pending', label: t('admin.questApproval.pendingRequests'), count: pendingQuestRequests.length, icon: ICONS.CLOCK },
            { id: 'approved', label: t('admin.questApproval.approvedRequests'), count: questRequests.filter(r => r.status === 'approved').length, icon: ICONS.CHECK_CIRCLE },
            { id: 'rejected', label: t('admin.questApproval.rejectedRequests'), count: questRequests.filter(r => r.status === 'rejected').length, icon: ICONS.TIMES_CIRCLE },
            { id: 'all', label: t('common.all', 'All'), count: questRequests.length, icon: ICONS.LIST },
          ]}
          activeTab={filterStatus}
          onTabChange={setFilterStatus}
        />
      }
      listContent={
        filteredRequests.length > 0 ? (
          filteredRequests.map(request => (
            <AdminListItem
              key={request.id}
              title={getLocalizedText(request.questData?.title, 'Untitled Quest')}
              selected={selectedRequest?.id === request.id}
              onClick={() => setSelectedRequest(request)}
              badges={[
                <QuestLabel key="status" type="status" value={request.status} text={t(`admin.questApproval.${request.status}`)} size="small" />,
                request.questData?.difficulty && <QuestLabel key="diff" type="difficulty" value={request.questData.difficulty} size="small" />,
              ].filter(Boolean)}
              meta={[
                { icon: ICONS.USER, text: getUsernameById(request.userId, users) },
                { icon: ICONS.CLOCK, text: formatters.shortDateTime(request.requestedAt) },
                { icon: ICONS.COINS, text: `${request.questData?.value || 0} XP` },
                request.questData?.category && { icon: ICONS.TAG, text: request.questData.category },
              ].filter(Boolean)}
            />
          ))
        ) : (
          <AdminEmptyState
            icon={ICONS.TARGET}
            description={
              filterStatus === 'pending' ? t('admin.questApproval.noPendingRequests') :
              filterStatus === 'approved' ? 'No approved requests' :
              filterStatus === 'rejected' ? 'No rejected requests' :
              'No quest requests found'
            }
            hint={filterStatus === 'pending' ? 'Quest requests will appear here when users submit quests for approval.' : undefined}
          />
        )
      }
      detailContent={
        selectedRequest ? (
          <div className="details-view">
            <div className="admin-detail-header">
              <h3>{getLocalizedText(selectedRequest.questData?.title, 'Untitled Quest')}</h3>
              <QuestLabel
                type="status"
                value={selectedRequest.status}
                text={t(`admin.questApproval.${selectedRequest.status}`)}
                size="small"
              />
            </div>

            <div className="details-content">
              <div className="admin-detail-section">
                <h4>{t('admin.questApproval.requestDetails')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('admin.questApproval.requestedBy')}:</label>
                    <span>{getUsernameById(selectedRequest.userId, users)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.questApproval.requestedAt')}:</label>
                    <span>{formatters.shortDateTime(selectedRequest.requestedAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.questApproval.questXP')}:</label>
                    <span>{selectedRequest.questData?.value || 0} XP</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.questApproval.questDifficulty')}:</label>
                    <QuestLabel type="difficulty" value={selectedRequest.questData?.difficulty || 'medium'} size="small" />
                  </div>
                  {selectedRequest.questData?.category && (
                    <div className="detail-item">
                      <label>{t('admin.questApproval.questCategory')}:</label>
                      <span>{selectedRequest.questData.category}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-detail-section">
                <h4>{t('admin.questApproval.questDescription')}</h4>
                <MarkdownDisplay content={getLocalizedText(selectedRequest.questData?.description, 'No description available')} />
              </div>

              {selectedRequest.status === 'approved' && selectedRequest.approvalMessage && (
                <div className="admin-detail-section">
                  <h4>{t('admin.questApproval.approvalMessage')}</h4>
                  <p>{selectedRequest.approvalMessage}</p>
                </div>
              )}

              {selectedRequest.status === 'rejected' && selectedRequest.rejectionMessage && (
                <div className="admin-detail-section">
                  <h4>{t('admin.questApproval.rejectionMessage')}</h4>
                  <p>{selectedRequest.rejectionMessage}</p>
                </div>
              )}
            </div>

            {selectedRequest.status === 'pending' && (
              <div className="admin-detail-actions">
                <button
                  className="approve-btn"
                  onClick={() => setShowApprovalModal(true)}
                >
                  <FontAwesomeIcon icon={ICONS.CHECK} />
                  {t('admin.questApproval.approveRequest')}
                </button>
                <button
                  className="reject-btn"
                  onClick={() => setShowRejectionModal(true)}
                >
                  <FontAwesomeIcon icon={ICONS.TIMES} />
                  {t('admin.questApproval.rejectRequest')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <AdminEmptyState
            icon={ICONS.CLIPBOARD_LIST}
            description="Select a request to view details"
          />
        )
      }
    >
      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="modal-overlay">
          <div className="modal approval-modal">
            <div className="modal-header">
              <h3>{t('admin.questApproval.approveRequest')}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowApprovalModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <p>{t('admin.questApproval.confirmApproval')}</p>
              <div className="form-group">
                <label>{t('admin.questApproval.approvalMessage')}</label>
                <textarea
                  value={approvalMessage}
                  onChange={(e) => setApprovalMessage(e.target.value)}
                  placeholder={t('admin.questApproval.approvalMessagePlaceholder')}
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="approve-btn"
                onClick={handleApproveRequest}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={ICONS.CHECK} />
                    {t('admin.questApproval.approveRequest')}
                  </>
                )}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowApprovalModal(false)}
                disabled={processing}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="modal-overlay">
          <div className="modal rejection-modal">
            <div className="modal-header">
              <h3>{t('admin.questApproval.rejectRequest')}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowRejectionModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <p>{t('admin.questApproval.confirmRejection')}</p>
              <div className="form-group">
                <label>{t('admin.questApproval.rejectionMessage')}</label>
                <textarea
                  value={rejectionMessage}
                  onChange={(e) => setRejectionMessage(e.target.value)}
                  placeholder={t('admin.questApproval.rejectionMessagePlaceholder')}
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="reject-btn"
                onClick={handleRejectRequest}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={ICONS.TIMES} />
                    {t('admin.questApproval.rejectRequest')}
                  </>
                )}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowRejectionModal(false)}
                disabled={processing}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
} 